/**
 * AuditLogFilters Component - Phase 2C-3
 *
 * Filter controls for audit log.
 * BSL Compliant: Neutral UI, factual filters only.
 */

import { AuditLogFilters as FilterType } from '../../types';
import { Select, Input, Button } from '../common';

interface AuditLogFiltersProps {
  filters: FilterType;
  onChange: (filters: FilterType) => void;
  actionTypes: string[];
  loadingActionTypes?: boolean;
  onReset: () => void;
}

const TARGET_TYPE_OPTIONS = [
  { value: '', label: 'All Targets' },
  { value: 'pool', label: 'Pool' },
  { value: 'membership', label: 'Membership' },
  { value: 'user', label: 'User' },
  { value: 'system', label: 'System' },
];

export function AuditLogFilters({
  filters,
  onChange,
  actionTypes,
  loadingActionTypes,
  onReset,
}: AuditLogFiltersProps): JSX.Element {
  const actionOptions = [
    { value: '', label: 'All Actions' },
    ...actionTypes.map((action) => ({
      value: action,
      label: action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    })),
  ];

  const handleChange = (key: keyof FilterType, value: string) => {
    onChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  const hasFilters = Object.values(filters).some((v) => v !== undefined && v !== '');

  return (
    <div className="space-y-4 p-4 bg-slate-800 rounded-lg">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-300">Filters</h3>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={onReset}>
            Reset
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Action Type */}
        <Select
          label="Action"
          value={filters.action || ''}
          onChange={(value) => handleChange('action', value)}
          options={actionOptions}
          disabled={loadingActionTypes}
        />

        {/* Target Type */}
        <Select
          label="Target Type"
          value={filters.target_type || ''}
          onChange={(value) => handleChange('target_type', value)}
          options={TARGET_TYPE_OPTIONS}
        />

        {/* Admin User */}
        <Input
          label="Admin User"
          value={filters.admin_user_id || ''}
          onChange={(value) => handleChange('admin_user_id', value)}
          placeholder="Filter by admin..."
        />

        {/* Target ID */}
        <Input
          label="Target ID"
          value={filters.target_id || ''}
          onChange={(value) => handleChange('target_id', value)}
          placeholder="Filter by target..."
        />
      </div>

      {/* Date Range */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Start Date"
          type="datetime-local"
          value={filters.start_date || ''}
          onChange={(value) => handleChange('start_date', value)}
        />

        <Input
          label="End Date"
          type="datetime-local"
          value={filters.end_date || ''}
          onChange={(value) => handleChange('end_date', value)}
        />
      </div>
    </div>
  );
}

// Also export as named for AuditLogFilters component name compatibility
export { AuditLogFilters as default };
